<script setup lang="ts">
import { Chart } from 'chart.js/auto';
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { ComparisonResult } from '../lib/types';

const props = defineProps<{
  result: ComparisonResult;
}>();

const portfolioCanvas = ref<HTMLCanvasElement | null>(null);
const taxesCanvas = ref<HTMLCanvasElement | null>(null);
const contribCanvas = ref<HTMLCanvasElement | null>(null);

let portfolioChart: Chart | null = null;
let taxesChart: Chart | null = null;
let contribChart: Chart | null = null;

function buildCharts() {
  const labels = props.result.jdg.portfolioSeries.map((_, idx) => `M${idx + 1}`);

  if (portfolioChart) portfolioChart.destroy();
  if (taxesChart) taxesChart.destroy();
  if (contribChart) contribChart.destroy();

  portfolioChart = new Chart(portfolioCanvas.value as HTMLCanvasElement, {
    type: 'line',
    data: {
      labels,
      datasets: [
        { label: 'JDG portfolio', data: props.result.jdg.portfolioSeries, borderColor: '#2563eb', fill: false },
        { label: 'Sp. z o.o. portfolio', data: props.result.spzoo.portfolioSeries, borderColor: '#16a34a', fill: false }
      ]
    },
    options: {
      responsive: true,
      scales: { y: { beginAtZero: true } }
    }
  });

  taxesChart = new Chart(taxesCanvas.value as HTMLCanvasElement, {
    type: 'line',
    data: {
      labels,
      datasets: [
        { label: 'JDG cumulative taxes', data: props.result.jdg.taxSeries, borderColor: '#f97316', fill: false },
        { label: 'Sp. z o.o. cumulative taxes', data: props.result.spzoo.taxSeries, borderColor: '#0ea5e9', fill: false }
      ]
    },
    options: {
      responsive: true,
      scales: { y: { beginAtZero: true } }
    }
  });

  contribChart = new Chart(contribCanvas.value as HTMLCanvasElement, {
    type: 'line',
    data: {
      labels,
      datasets: [
        { label: 'JDG cumulative contributions', data: props.result.jdg.contributionSeries, borderColor: '#8b5cf6', fill: false },
        { label: 'Sp. z o.o. cumulative contributions', data: props.result.spzoo.contributionSeries, borderColor: '#22c55e', fill: false }
      ]
    },
    options: {
      responsive: true,
      scales: { y: { beginAtZero: true } }
    }
  });
}

onMounted(buildCharts);
watch(() => props.result, buildCharts, { deep: true });
onBeforeUnmount(() => {
  portfolioChart?.destroy();
  taxesChart?.destroy();
  contribChart?.destroy();
});
</script>

<template>
  <div class="card">
    <h3>Charts</h3>
    <div class="grid">
      <div>
        <h4>Portfolio value</h4>
        <canvas ref="portfolioCanvas"></canvas>
      </div>
      <div>
        <h4>Cumulative taxes paid</h4>
        <canvas ref="taxesCanvas"></canvas>
      </div>
      <div>
        <h4>Cumulative contributions</h4>
        <canvas ref="contribCanvas"></canvas>
      </div>
    </div>
  </div>
</template>
